// Artifact transit, on the device (Concept: "Artifacts: where the picture goes"; Build spec
// Architecture, "Artifact transit"). The holder encrypts the artifact with a per-claim
// AES-256-GCM key; the rail stores only ciphertext. The key travels only in the vouch link's
// fragment (#k=…), which browsers never send to a server; the attester decrypts on the
// device and checks the plaintext against the claim's artifact_hash.
const subtle = () => globalThis.crypto.subtle;
const IV_BYTES = 12;

export const MAX_ARTIFACT_BYTES = 10 * 1024 * 1024;

const b64u = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromB64u = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0));

/** "sha256:<hex>", as the claim's artifact_hash. */
export async function sha256Hex(data: Uint8Array): Promise<string> {
  const d = new Uint8Array(await subtle().digest("SHA-256", data));
  return "sha256:" + Array.from(d, (x) => x.toString(16).padStart(2, "0")).join("");
}

/** A fresh 256-bit key for one claim's artifact. */
export async function newArtifactKey(): Promise<{ raw: Uint8Array }> {
  return { raw: globalThis.crypto.getRandomValues(new Uint8Array(32)) };
}

const importKey = (raw: Uint8Array, usage: KeyUsage) => subtle().importKey("raw", raw, { name: "AES-GCM" }, false, [usage]);

/** IV (12 bytes) || AES-256-GCM ciphertext and tag. */
export async function encryptArtifact(raw: Uint8Array, plain: Uint8Array): Promise<Uint8Array> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ct = new Uint8Array(await subtle().encrypt({ name: "AES-GCM", iv }, await importKey(raw, "encrypt"), plain));
  const out = new Uint8Array(iv.length + ct.length);
  out.set(iv); out.set(ct, iv.length);
  return out;
}

export async function decryptArtifact(raw: Uint8Array, blob: Uint8Array): Promise<Uint8Array> {
  const iv = blob.slice(0, IV_BYTES), ct = blob.slice(IV_BYTES);
  return new Uint8Array(await subtle().decrypt({ name: "AES-GCM", iv }, await importKey(raw, "decrypt"), ct));
}

export type Verdict = { ok: true; plain: Uint8Array } | { ok: false; reason: "decrypt_failed" | "hash_mismatch" };

/** Decrypt, hash the plaintext, compare with the claim's artifact_hash. */
export async function verifyArtifact(raw: Uint8Array, blob: Uint8Array, artifactHash: string): Promise<Verdict> {
  let plain: Uint8Array;
  try { plain = await decryptArtifact(raw, blob); } catch { return { ok: false, reason: "decrypt_failed" }; }
  return (await sha256Hex(plain)) === artifactHash ? { ok: true, plain } : { ok: false, reason: "hash_mismatch" };
}

/** The vouch link with the key in its fragment (and only there). */
export function linkWithKey(link: string, raw: Uint8Array): string {
  const u = new URL(link);
  const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
  frag.set("k", b64u(raw));
  return `${u.origin}${u.pathname}#${frag.toString()}`;
}

/** The key from a fragment (#k=…, possibly beside other fragment fields), or null. */
export function keyFromFragment(hash: string): Uint8Array | null {
  const k = new URLSearchParams(hash.replace(/^#/, "")).get("k");
  if (!k || !/^[A-Za-z0-9_-]{43}$/.test(k)) return null;
  const raw = fromB64u(k);
  return raw.length === 32 ? raw : null;
}
